"use client";

import { useRef, useState, type ReactNode } from "react";
import { Download } from "lucide-react";
import { Dropzone } from "@/components/Dropzone";
import { Button, Card, ErrorNote, Progress } from "@/components/ui";
import { getFfmpeg, kokAd } from "@/lib/ffmpeg";
import { formatBytes } from "@/lib/utils";

type Komut = { args: string[]; uzanti: string; mime: string } | { hata: string };

/**
 * ffmpeg tabanlı video araçları için ortak kabuk: yükleme, önizleme, ilerleme,
 * çalıştırma, çıktı önizleme/indirme. Her araç yalnızca ayar arayüzünü ve
 * ffmpeg komutunu sağlar. Video sunucuya gitmez; her şey tarayıcıda çalışır.
 */
export function VideoAraci({
  accept = "video/*",
  yuklemeEtiket = "Videonuzu sürükleyin",
  ipucu = "Video tarayıcınızdan çıkmaz · ilk kullanımda motor ~31 MB iner",
  calistirEtiket = "Dönüştür",
  calistirIkon,
  ekAd = "cikti",
  oynatici = true,
  komut,
  ayarlar,
}: {
  accept?: string;
  yuklemeEtiket?: string;
  ipucu?: string;
  calistirEtiket?: string;
  calistirIkon?: ReactNode;
  ekAd?: string;
  oynatici?: boolean;
  /** Yüklenen dosyaya göre ffmpeg komutunu üretir (girdi adı "girdi", çıktı "cikti.<uzanti>"). */
  komut: () => Komut;
  /** Ayar arayüzü (araç kendi durumunu tutar). */
  ayarlar?: ReactNode;
}) {
  const dosya = useRef<File | null>(null);
  const [url, setUrl] = useState("");
  const [ad, setAd] = useState("video");
  const [eski, setEski] = useState(0);
  const [ilerleme, setIlerleme] = useState<number | null>(null);
  const [durum, setDurum] = useState("");
  const [cikti, setCikti] = useState<{ url: string; boyut: number; uzanti: string; mime: string } | null>(null);
  const [hata, setHata] = useState("");

  const yukle = (files: File[]) => {
    dosya.current = files[0];
    setUrl(URL.createObjectURL(files[0]));
    setAd(kokAd(files[0].name));
    setEski(files[0].size);
    setCikti(null);
    setHata("");
  };

  async function calistir() {
    if (!dosya.current) return;
    const k = komut();
    if ("hata" in k) {
      setHata(k.hata);
      return;
    }
    setHata("");
    setCikti(null);
    setDurum("Video motoru hazırlanıyor…");
    setIlerleme(0);
    try {
      const ff = await getFfmpeg();
      setDurum("İşleniyor…");
      ff.on("progress", ({ progress }) => setIlerleme(Math.min(100, Math.round(progress * 100))));
      await ff.writeFile("girdi", new Uint8Array(await dosya.current.arrayBuffer()));
      await ff.exec([...k.args]);
      const data = await ff.readFile(`cikti.${k.uzanti}`);
      const blob = new Blob([data as unknown as BlobPart], { type: k.mime });
      setCikti({ url: URL.createObjectURL(blob), boyut: blob.size, uzanti: k.uzanti, mime: k.mime });
      await ff.deleteFile("girdi").catch(() => {});
      await ff.deleteFile(`cikti.${k.uzanti}`).catch(() => {});
    } catch {
      setHata("İşlem başarısız oldu. Farklı bir video, biçim ya da daha kısa bir video deneyin.");
    } finally {
      setDurum("");
      setIlerleme(null);
    }
  }

  if (!url) {
    return (
      <>
        <Dropzone accept={accept} label={yuklemeEtiket} hint={ipucu} onFiles={yukle} />
        <div className="mt-4"><ErrorNote>{hata}</ErrorNote></div>
      </>
    );
  }

  return (
    <div className="space-y-4">
      {oynatici && (
        <Card>
          { }
          <video src={url} controls className="max-h-[46vh] w-full rounded-xl bg-black" />
        </Card>
      )}

      {ayarlar}

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={calistir} loading={ilerleme !== null}>
          {calistirIkon}
          {calistirEtiket}
        </Button>
        <button
          onClick={() => { dosya.current = null; setUrl(""); setCikti(null); setHata(""); }}
          className="text-sm text-muted underline"
        >
          Başka video
        </button>
      </div>

      {ilerleme !== null && <Progress value={ilerleme / 100} label={durum || `%${ilerleme}`} />}
      <ErrorNote>{hata}</ErrorNote>

      {cikti && (
        <Card className="space-y-3">
          {cikti.mime.startsWith("audio") ? (
             
            <audio src={cikti.url} controls className="w-full" />
          ) : cikti.mime === "image/gif" ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={cikti.url} alt="Sonuç" className="mx-auto max-h-80 rounded-lg" />
          ) : (
             
            <video src={cikti.url} controls className="max-h-[46vh] w-full rounded-xl bg-black" />
          )}
          <div className="flex flex-wrap items-center gap-3">
            <a
              href={cikti.url}
              download={`${ad}-${ekAd}.${cikti.uzanti}`}
              className="inline-flex items-center gap-2 rounded-xl bg-brand px-5 py-2.5 text-sm font-medium text-brand-fg"
            >
              <Download className="size-4" /> İndir
            </a>
            <span className="text-sm text-muted">
              {formatBytes(cikti.boyut)}
              {eski > 0 && cikti.mime.startsWith("video") ? ` · önceki: ${formatBytes(eski)}` : ""}
            </span>
          </div>
        </Card>
      )}

      <p className="text-sm text-muted">Her şey tarayıcınızda yapılır; video sunucuya gitmez. GPU olmadığı için uzun videolar yavaş işlenebilir.</p>
    </div>
  );
}
